// import { useEffect } from "react";

import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { motion } from "framer-motion";
import { useWindowSize } from "react-use";

import Checkin from "../../assets/icons/CheckIn.svg";
import Checkout from "../../assets/icons/CheckOut.svg";
import Food from "../../assets/icons/Food.svg";
import Parcel from "../../assets/icons/Parcel.svg";
import Call from "../../assets/icons/Call.svg";
import NoAppointment from "../../assets/icons/NoAppointment.svg";
import Overview from "../../assets/icons/Overview.svg";
import Wifi from "../../assets/icons/Wifi.svg";

import Title from "../../components/Title";
import Bump from "../../components/Animations/Bump";
import Curve from "../../components/Animations/PageTransitions/Curve";

// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";

import useIdleEffect from "../../bit/board/hooks/useIdleEffect";
import useQueryBoard, { useLayout } from "../../lib/hooks/useQueryBoard";
import { useOfflineContext } from "../../context/Offline";

const WifiButton = styled(motion.button)(({ theme }) => ({
  position: "absolute",
  right: "3rem",
  bottom: "21rem",
  display: "flex",
  alignItems: "center",
  margin: "2rem",
  padding: "2.5rem 3rem",
  borderRadius: "0.5rem",
  backgroundColor: theme.palette.primary.main,
  zIndex: 1000,

  svg: {
    fill: theme.palette.background.default,
    marginRight: "2rem",
  },

  h4: {
    color: theme.palette.background.default,
  },

  "&:active": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:disabled": {
    backgroundColor: theme.palette.grey[400],
  },

  "@media screen and (orientation: landscape)": {
    bottom: "14rem",
  },
}));

export const MUIHomeButtonGroup = styled("div")({
  maxWidth: "90.4rem",
  width: "96%",
  display: "flex",
  flexDirection: "column",
  padding: "4.8rem 0",

  "@media screen and (orientation: landscape)": {
    maxWidth: "180.8rem",
    flexDirection: "row",
    padding: "0",

    "> div": {
      width: "50%",
    },
  },
});

const HomeGrid = styled(Grid)({
  height: "68%",
  margin: "3rem",
  alignItems: "stretch",

  "@media screen and (orientation: landscape)": {
    margin: "0",
    height: "80%",
  },
});

export const MUIHomeButton = styled(motion.button)(({ theme }) => ({
  display: "block",
  width: "40rem",
  margin: "3.2rem",
  padding: "4rem 1.5rem",
  borderRadius: "1rem",
  backgroundColor: theme.palette.primary.main,

  svg: {
    fill: theme.palette.background.default,
    marginBottom: "3.2rem",
  },

  h2: {
    color: theme.palette.background.default,
  },

  "&:active": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:disabled": {
    backgroundColor: theme.palette.grey[400],
  },

  "@media screen and (orientation: landscape)": {
    margin: "0 3.2rem",
  },
}));

export const MUIHomeSmallButton = styled(motion.button)(({ theme }) => ({
  margin: "2rem auto",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4rem 1rem",
  borderRadius: "1rem",
  backgroundColor: theme.palette.primary.main,
  width: "65%",

  svg: {
    fill: theme.palette.background.default,
    position: "absolute",
    left: "4rem",
  },

  h3: {
    color: theme.palette.background.default,
  },

  "&:active": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "@media screen and (orientation: landscape)": {
    width: "30%",
    margin: "0.5rem 3rem",
    maxWidth: "70rem",
  },
}));

const HomePage = () => {
  const navigate = useNavigate();

  const { width, height } = useWindowSize();

  const orientation = width < height ? "portrait" : "landscape";

  const { pathname } = useLocation();

  const mainRoute = pathname === "/";

  const { data, isLoading } = useQueryBoard();
  const { t } = useTranslation(["home"]);

  const { online } = useOfflineContext();

  const {
    welcomeMessage,
    checkin,
    independentManualCheckin,
    integratedManualCheckin,
    checkout,
    manualCheckout,
    packageDelivery,
    foodDelivery,
    callReception,
    overview,
    wifi,
  } = useLayout();

  const welcomeMessageText = welcomeMessage?.text || t("home:welcomeMessage");
  const checkinText = checkin?.text || t("home:checkIn");
  const independentManualCheckinText =
    independentManualCheckin?.text || t("home:independentManualCheckIn");
  const checkoutText = checkout?.text || t("home:checkOut");
  const manualCheckoutText = manualCheckout?.text || t("home:manualCheckOut");
  const packageDeliveryText = packageDelivery?.text || t("home:parcelDelivery");
  const foodDeliveryText = foodDelivery?.text || t("home:foodDelivery");
  const callReceptionText = callReception?.text || t("home:callReception");
  const overviewText = overview?.text || t("home:overview");
  const wifiText = wifi?.text || "Wi-Fi";

  const moreThan4Buttons =
    [
      checkin?.enabled,
      independentManualCheckin?.enabled,
      checkout?.enabled || manualCheckout?.enabled,
      packageDelivery?.enabled,
      foodDelivery?.enabled,
      callReception?.enabled,
      overview?.enabled,
    ].filter(Boolean).length > 4;

  // usePageTracking({
  //   page: "home",
  // });

  // const { trackEvent } = useEventTracking();

  useIdleEffect(() => {
    // we check the pathname for a trailing slash
    // because of service worker scope issue
    // https://github.com/w3c/ServiceWorker/issues/1272
    //
    // if there is no trailing slash, we redirect the user
    if (window.location.pathname.endsWith("/")) {
      window.location.reload();
      return;
    }

    window.location.href = `${window.location.pathname}/`;
  }, 300000);

  // useEffect(() => {
  //   if (isDev) {
  //     return;
  //   }
  //
  //   navigate(`${pathname}/`, { replace: true });
  // }, []);

  if (!data && !isLoading) {
    return (
      <Box className="d-flex align-items-center justify-content-center h-100">
        <Typography variant="h1">{t("home:unavailable")}</Typography>
      </Box>
    );
  }

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <Title className="p-0 mx-3" disableAnimation={!mainRoute}>
          {welcomeMessage?.enabled && (
            <Typography variant="h1">{welcomeMessageText}</Typography>
          )}
        </Title>
        <HomeGrid
          container
          justifyContent="center"
          alignItems="center"
          rowGap={orientation === "landscape" ? 4 : 0}
        >
          {!moreThan4Buttons ? (
            <>
              {checkin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });

                      if (integratedManualCheckin?.enabled) {
                        return navigate("/access/option/checkin", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkin", { replace: true });
                    }}
                  >
                    <Checkin width="10rem" />
                    <Typography variant="h2">{checkinText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {independentManualCheckin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });
                      return navigate("/access/checkin/manual", {
                        replace: true,
                      });
                    }}
                  >
                    <NoAppointment width="10rem" />
                    <Typography variant="h2">
                      {independentManualCheckinText}
                    </Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {(checkout?.enabled || manualCheckout?.enabled) && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkout" });
                      if (checkout?.enabled && manualCheckout?.enabled) {
                        return navigate("/access/option/checkout", {
                          replace: true,
                        });
                      }

                      if (manualCheckout?.enabled) {
                        return navigate("/access/checkout/manual", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkout", { replace: true });
                    }}
                  >
                    <Checkout width="10rem" />
                    <Typography variant="h2">
                      {checkout?.enabled ? checkoutText : manualCheckoutText}
                    </Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {packageDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "package" });

                      if (packageDelivery?.isCustom) {
                        return navigate("/custom-delivery/package", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/package", { replace: true });
                    }}
                  >
                    <Parcel width="10rem" />
                    <Typography variant="h2">{packageDeliveryText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {foodDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "food" });

                      if (foodDelivery?.isCustom) {
                        return navigate("/custom-delivery/food", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/food", { replace: true });
                    }}
                  >
                    <Food width="10rem" />
                    <Typography variant="h2">{foodDeliveryText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {overview?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      return navigate("/overview", {
                        replace: true,
                      });
                    }}
                  >
                    <Overview width="10rem" />
                    <Typography variant="h2">{overviewText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {callReception?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    disabled={!online}
                    onClick={() => {
                      return navigate("/contact/reception/call", {
                        replace: true,
                      });
                    }}
                  >
                    <Call width="10rem" />
                    <Typography variant="h2">{callReceptionText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
            </>
          ) : (
            <>
              {checkin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });

                      if (integratedManualCheckin?.enabled) {
                        return navigate("/access/option/checkin", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkin", { replace: true });
                    }}
                  >
                    <Checkin width="6.5rem" />
                    <Typography variant="h3">{checkinText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {independentManualCheckin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });
                      return navigate("/access/checkin/manual", {
                        replace: true,
                      });
                    }}
                  >
                    <NoAppointment width="6.5rem" />
                    <Typography variant="h3">
                      {independentManualCheckinText}
                    </Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {(checkout?.enabled || manualCheckout?.enabled) && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkout" });
                      if (checkout?.enabled && manualCheckout?.enabled) {
                        return navigate("/access/option/checkout", {
                          replace: true,
                        });
                      }

                      if (manualCheckout?.enabled) {
                        return navigate("/access/checkout/manual", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkout", { replace: true });
                    }}
                  >
                    <Checkout width="6.5rem" />
                    <Typography variant="h3">
                      {checkout?.enabled ? checkoutText : manualCheckoutText}
                    </Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {packageDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "package" });

                      if (packageDelivery?.isCustom) {
                        return navigate("/custom-delivery/package", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/package", { replace: true });
                    }}
                  >
                    <Parcel width="6.5rem" />
                    <Typography variant="h3">{packageDeliveryText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {foodDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "food" });

                      if (foodDelivery?.isCustom) {
                        return navigate("/custom-delivery/food", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/food", { replace: true });
                    }}
                  >
                    <Food width="6.5rem" />
                    <Typography variant="h3">{foodDeliveryText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {overview?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      return navigate("/overview", {
                        replace: true,
                      });
                    }}
                  >
                    <Overview width="6.5rem" />
                    <Typography variant="h3">{overviewText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {callReception?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      return navigate("/contact/reception/call", {
                        replace: true,
                      });
                    }}
                  >
                    <Call width="6.5rem" />
                    <Typography variant="h3">{callReceptionText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
            </>
          )}
        </HomeGrid>
        {wifi?.enabled && (
          <Bump disabled={!mainRoute}>
            <WifiButton
              onClick={() => {
                navigate("/wifi", {
                  replace: true,
                });
              }}
            >
              <Wifi width="5.5rem" />
              <Typography variant="h4">{wifiText}</Typography>
            </WifiButton>
          </Bump>
        )}
      </div>
      <Outlet />
    </Curve>
  );
};

export default HomePage;
